<script setup lang="ts">
/**
 * AboutView — 1:1 порт docs/mockups/public-about.html (inbox-167).
 *
 * Секции (все из мокапа):
 *   1. Hero — badge «Российская компания · с 2025 года» + title с gradient
 *      span на «российского бизнеса» + sub
 *   2. Mission — 2-col: eyebrow «Миссия» + h2 + 2 параграфа vs 2×2 stats grid
 *      (12 команда / 600+ компаний / 15М кликов / 99.97% uptime)
 *   3. Team — 4-card grid (UiAvatar инициалы + name + role + bio):
 *      Егор Тактаев (CEO) / Михаил Орлов (CTO) / Анна Соколова (Head of Product)
 *      / Дмитрий Кузнецов (Head of ML)
 *   4. Legal — 2-col: «Реквизиты ООО» (ИНН/КПП/ОГРН/адрес/счёт/БИК) +
 *      «Соответствие требованиям» (Минцифры/152-ФЗ/хостинг/IT-аккред/УСН +
 *      5 compliance-badges)
 *   5. Contact — 2-col: info-card (email/телефон/Telegram/офис) + form-card
 *      (имя/контакт/тема-select/сообщение + submit)
 *
 * SEO/JSON-LD: usePageHead с productJsonLd (existing composable, see
 * inbox-101 SSR + inbox-167 spec § 33).
 *
 * Layout AppPublic уже даёт nav + footer (inbox-101) — здесь только
 * <section>ы внутри slot.
 */

import { ref } from 'vue';
import AppPublic from '@/layouts/AppPublic.vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiAvatar from '@/components/ui/UiAvatar.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import { usePageHead, productJsonLd } from '@/composables/usePageHead';

usePageHead({
  title: 'О компании',
  description: 'Команда, миссия, юридические данные, контакты. Russian-first B2B SaaS управления маркетинговыми ссылками.',
  path: '/about',
  type: 'website',
  jsonLd: productJsonLd(
    'О компании — link-platform',
    'Команда из 12 человек, инвестиции из РФ, серверы в Москве и Питере. Russian-first B2B SaaS управления маркетинговыми ссылками.',
  ),
});

// ─── Form state (frontend-only, submit заглушка до contact-backend'a) ──

const formName = ref('');
const formContact = ref('');
const formSubject = ref('Демо для команды');
const formMessage = ref('');
const formSent = ref(false);
const formError = ref<string | null>(null);

const SUBJECTS = [
  'Демо для команды',
  'Вопрос по тарифу',
  'Техническая поддержка',
  'Партнёрство',
  'Пресса / медиа',
];

function submitContact() {
  formError.value = null;
  if (!formName.value.trim() || !formContact.value.trim() || !formMessage.value.trim()) {
    formError.value = 'Заполните имя, контакт и сообщение.';
    return;
  }
  // Backend pending (POST /api/v1/public/contact-request — TODO).
  // Симуляция: показываем подтверждение, сбрасываем форму.
  formSent.value = true;
  formName.value = '';
  formContact.value = '';
  formMessage.value = '';
}

interface TeamMember {
  initials: string;
  name: string;
  role: string;
  bio: string;
}

const TEAM: TeamMember[] = [
  { initials: 'ЕТ', name: 'Егор Тактаев',     role: 'CEO, фаундер',     bio: '10 лет в performance-маркетинге, Авито, Тинькофф Бизнес' },
  { initials: 'МО', name: 'Михаил Орлов',     role: 'CTO, фаундер',     bio: 'Бывший backend-lead Yandex Cloud, ClickHouse contributor' },
  { initials: 'АС', name: 'Анна Соколова',    role: 'Head of Product',  bio: 'Продукт-аналитика в VK Реклама и Skillbox' },
  { initials: 'ДК', name: 'Дмитрий Кузнецов', role: 'Head of ML',       bio: 'PhD по машинному обучению, антифрод в Тинькофф' },
];

interface Stat { value: string; label: string }
const STATS: Stat[] = [
  { value: '12',     label: 'человек в команде' },
  { value: '600+',   label: 'компаний используют' },
  { value: '15М',    label: 'кликов / месяц' },
  { value: '99.97%', label: 'uptime за полгода' },
];

interface LegalRow { label: string; value: string }
const LEGAL_INFO: LegalRow[] = [
  { label: 'Юр. название', value: 'ООО «Линк-Платформа»' },
  { label: 'ИНН / КПП',    value: '7712345678 / 771201001' },
  { label: 'ОГРН',         value: '1257700123456' },
  { label: 'Юр. адрес',    value: '125009, Москва, ул. Тверская, д. 7, оф. 414' },
  { label: 'Расч. счёт',   value: '40702810000000123456 в Тинькофф Банк' },
  { label: 'БИК',          value: '044525974' },
];

const COMPLIANCE_INFO: LegalRow[] = [
  { label: 'Реестр Минцифры', value: '№ 12345 от 2026-03-15' },
  { label: '152-ФЗ ПДн',      value: 'Уведомлен РКН, 77-19-007654' },
  { label: 'Хостинг',         value: 'Yandex Cloud (Москва, СПб)' },
  { label: 'IT-аккредитация', value: 'Минцифры, рег. № 17-А' },
  { label: 'УСН',             value: 'УСН-доходы 6%, IT-льготы' },
];

const COMPLIANCE_BADGES = ['152-ФЗ', 'Минцифры', 'ОФД', 'SLA 99.9%', 'IT-аккред.'];
</script>

<template>
  <AppPublic>
    <!-- 1. HERO -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-badge">Российская компания · с 2025 года</div>
          <h1 class="hero-title">
            Делаем умную работу со ссылками<br />
            для <span class="gradient">российского бизнеса</span>
          </h1>
          <p class="hero-sub">
            Команда из 12 человек, инвестиции из РФ, серверы в Москве и Питере.
            Наша цель — заменить Bitly и Linktree для отечественного рынка.
          </p>
        </div>
      </div>
    </section>

    <!-- 2. MISSION -->
    <section class="section-mission">
      <div class="container">
        <div class="mission-grid">
          <div class="mission-text">
            <span class="section-eyebrow">Миссия</span>
            <h2>Почему мы это строим</h2>
            <p>
              Когда в 2024 году иностранные сервисы начали ограничивать доступ российским
              компаниям, рынок остался без серьёзного инструмента управления маркетинговыми
              ссылками. Bitly Enterprise недоступен, Rebrandly недоступен, Linktree
              ограничен — а российских альтернатив enterprise-уровня просто не было.
            </p>
            <p>
              Мы делаем продукт, который не уступает зарубежным аналогам, но при этом
              работает целиком в российской юрисдикции. AI-копайлот на YandexGPT,
              серверы в РФ, нативные интеграции с экосистемой Yandex / VK / 1С /
              Bitrix24. Кому подойдёт — performance-маркетологам, агентствам, медиа,
              крупным брендам с офлайн-каналами.
            </p>
          </div>
          <div class="mission-visual">
            <div class="mission-stats">
              <div v-for="s in STATS" :key="s.label" class="mission-stat">
                <div class="v">{{ s.value }}</div>
                <div class="l">{{ s.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. TEAM -->
    <section class="team-section">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Команда</span>
          <h2>Кто строит link-platform</h2>
          <p class="section-sub">Маленькая команда с опытом из Yandex, VK, Тинькофф и Авито.</p>
        </div>
        <div class="team-grid">
          <article v-for="m in TEAM" :key="m.name" class="team-card">
            <div class="team-photo">
              <UiAvatar :name="m.initials" size="lg" />
            </div>
            <div class="team-name">{{ m.name }}</div>
            <div class="team-role">{{ m.role }}</div>
            <div class="team-bio">{{ m.bio }}</div>
          </article>
        </div>
      </div>
    </section>

    <!-- 4. LEGAL & COMPLIANCE -->
    <section class="section-legal">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Юридическая информация</span>
          <h2>Реквизиты и compliance</h2>
          <p class="section-sub">Всё открыто: реквизиты юр.лица, лицензии, статус в реестрах.</p>
        </div>

        <div class="legal-grid">
          <UiCard class="legal-card">
            <template #header>
              <h3 class="legal-h">
                <span class="ic">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6v6H9z"/></svg>
                </span>
                Реквизиты ООО
              </h3>
            </template>
            <div v-for="row in LEGAL_INFO" :key="row.label" class="legal-row">
              <span class="label">{{ row.label }}</span>
              <span class="value">{{ row.value }}</span>
            </div>
          </UiCard>

          <UiCard class="legal-card">
            <template #header>
              <h3 class="legal-h">
                <span class="ic">
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                </span>
                Соответствие требованиям
              </h3>
            </template>
            <div v-for="row in COMPLIANCE_INFO" :key="row.label" class="legal-row">
              <span class="label">{{ row.label }}</span>
              <span class="value">{{ row.value }}</span>
            </div>
            <div class="compliance-badges">
              <UiBadge v-for="b in COMPLIANCE_BADGES" :key="b" variant="success" dot>{{ b }}</UiBadge>
            </div>
          </UiCard>
        </div>
      </div>
    </section>

    <!-- 5. CONTACT -->
    <section class="section-contact">
      <div class="container">
        <div class="section-head">
          <span class="section-eyebrow">Контакты</span>
          <h2>Свяжитесь с нами</h2>
          <p class="section-sub">Отвечаем в течение 1 часа в рабочее время. Срочные вопросы — Telegram.</p>
        </div>

        <div class="contact-grid">
          <div class="contact-info">
            <h3>Способы связи</h3>
            <div class="contact-row">
              <div class="ic">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              </div>
              <div>
                <div class="label">Поддержка / общие вопросы</div>
                <div class="value"><a href="mailto:info@link-platform.ru">info@link-platform.ru</a></div>
              </div>
            </div>
            <div class="contact-row">
              <div class="ic">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </div>
              <div>
                <div class="label">Продажи (B2B + Enterprise)</div>
                <div class="value"><a href="tel:+74951234567">+7 (495) 123-45-67</a></div>
              </div>
            </div>
            <div class="contact-row">
              <div class="ic">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.5 4.5 2.5 12.5l7 2 2 7 10-17z"/></svg>
              </div>
              <div>
                <div class="label">Telegram</div>
                <div class="value"><a href="https://t.me/link_platform_help" target="_blank" rel="noopener">@link_platform_help</a></div>
              </div>
            </div>
            <div class="contact-row">
              <div class="ic">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <div class="label">Офис</div>
                <div class="value">Москва, ул. Тверская, д. 7, оф. 414</div>
              </div>
            </div>
          </div>

          <form class="contact-form" @submit.prevent="submitContact">
            <h3>Напишите нам</h3>
            <p class="form-sub">Заполните форму и мы свяжемся в течение часа.</p>

            <div v-if="formSent" class="form-success">
              Заявка принята. Мы свяжемся в течение часа на указанный контакт.
              <button type="button" class="reset-link" @click="formSent = false">Отправить ещё одну</button>
            </div>

            <template v-else>
              <div class="field">
                <label for="contact-name">Ваше имя</label>
                <input id="contact-name" v-model="formName" type="text" placeholder="Имя Фамилия" autocomplete="name" />
              </div>
              <div class="field">
                <label for="contact-contact">Email или телефон</label>
                <input id="contact-contact" v-model="formContact" type="text" placeholder="you@example.com" autocomplete="email" />
              </div>
              <div class="field">
                <label for="contact-subject">Тема обращения</label>
                <select id="contact-subject" v-model="formSubject">
                  <option v-for="s in SUBJECTS" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>
              <div class="field">
                <label for="contact-message">Сообщение</label>
                <textarea id="contact-message" v-model="formMessage" placeholder="Расскажите, чем мы можем помочь…"></textarea>
              </div>
              <p v-if="formError" class="form-error">{{ formError }}</p>
              <button type="submit" class="btn-primary contact-submit">Отправить</button>
            </template>
          </form>
        </div>
      </div>
    </section>
  </AppPublic>
</template>

<style scoped>
.container { max-width: 1180px; margin: 0 auto; padding: 0 32px; }

/* ─── HERO (per STYLE_NOTES §1) ─── */
.hero { position: relative; padding: 88px 0 56px; overflow: hidden; }
.hero::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(ellipse 60% 50% at 30% 20%, rgba(79, 140, 255, 0.15), transparent 70%),
    radial-gradient(ellipse 50% 40% at 80% 30%, rgba(124, 92, 255, 0.10), transparent 70%);
}
.hero-content { position: relative; max-width: 760px; margin: 0 auto; text-align: center; }
.hero-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px; margin-bottom: 24px;
  background: rgba(79, 140, 255, 0.12);
  border: 1px solid rgba(79, 140, 255, 0.25);
  border-radius: 100px;
  font-size: 13px; color: #6ea1ff; font-weight: 500;
}
.hero-badge::before {
  content: ''; width: 6px; height: 6px; border-radius: 50%;
  background: #4f8cff; box-shadow: 0 0 8px #4f8cff;
}
.hero-title {
  font-size: 56px; line-height: 1.08; letter-spacing: -0.025em;
  font-weight: 700; margin-bottom: 24px;
}
.hero-title .gradient {
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.hero-sub {
  font-size: 19px; line-height: 1.55; color: #8b95a5;
  max-width: 640px; margin: 0 auto;
}

/* ─── Section-level (per STYLE_NOTES §2) ─── */
.section-mission, .team-section, .section-legal, .section-contact { padding: 80px 0; }
.section-head { text-align: center; margin-bottom: 56px; }
.section-eyebrow {
  display: inline-block; font-size: 13px; font-weight: 600;
  color: #6ea1ff; text-transform: uppercase; letter-spacing: 0.08em;
  margin-bottom: 14px;
}
:deep(h2), .section-mission h2, .team-section h2, .section-legal h2, .section-contact h2 {
  font-size: 38px; line-height: 1.15; letter-spacing: -0.02em;
  font-weight: 700; margin-bottom: 14px;
}
.section-sub {
  font-size: 17px; color: #8b95a5; max-width: 580px; margin: 0 auto;
}

/* ─── MISSION ─── */
.mission-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 56px;
  align-items: center; max-width: 1000px; margin: 0 auto;
}
.mission-text h2 { text-align: left; }
.mission-text p {
  font-size: 16px; color: #8b95a5; line-height: 1.75; margin-bottom: 14px;
}
.mission-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.mission-stat {
  padding: 24px; background: #161c26;
  border: 1px solid #1f2733; border-radius: 12px;
}
.mission-stat .v {
  font-size: 34px; font-weight: 700; letter-spacing: -0.02em; line-height: 1;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent;
}
.mission-stat .l { font-size: 13px; color: #8b95a5; margin-top: 8px; line-height: 1.5; }

/* ─── TEAM ─── */
.team-section {
  background: #11161e;
  border-top: 1px solid #1f2733;
  border-bottom: 1px solid #1f2733;
}
.team-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.team-card {
  background: #161c26; border: 1px solid #1f2733; border-radius: 14px;
  padding: 28px 22px; text-align: center; transition: all 0.15s;
}
.team-card:hover { border-color: #4f8cff; transform: translateY(-1px); }
.team-photo { display: flex; justify-content: center; margin-bottom: 14px; }
.team-photo :deep(.ui-avatar) {
  width: 88px; height: 88px; font-size: 26px;
  background: linear-gradient(135deg, #4f8cff 0%, #7c5cff 100%);
  color: #fff; font-weight: 700;
}
.team-name { font-size: 16px; font-weight: 600; margin-bottom: 4px; }
.team-role { font-size: 13px; color: #8b95a5; margin-bottom: 10px; }
.team-bio { font-size: 12.5px; color: #5a6577; line-height: 1.5; }

/* ─── LEGAL ─── */
.legal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
.legal-card { padding: 0; }
.legal-h {
  font-size: 18px; font-weight: 600; margin: 0;
  display: flex; align-items: center; gap: 10px;
}
.legal-h .ic {
  width: 32px; height: 32px; border-radius: 8px;
  background: rgba(79, 140, 255, 0.12); color: #6ea1ff;
  display: grid; place-items: center;
}
.legal-row {
  display: grid; grid-template-columns: 140px 1fr; gap: 14px;
  padding: 10px 0; border-bottom: 1px solid #1f2733; font-size: 13.5px;
}
.legal-row:last-child { border-bottom: 0; }
.legal-row .label { color: #8b95a5; }
.legal-row .value {
  color: #e8edf3; font-family: 'SF Mono', ui-monospace, monospace; font-size: 13px;
}
.compliance-badges {
  display: flex; flex-wrap: wrap; gap: 8px; margin-top: 18px;
}

/* ─── CONTACT ─── */
.contact-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 40px;
  max-width: 1000px; margin: 0 auto;
}
.contact-info {
  background: #161c26; border: 1px solid #1f2733;
  border-radius: 14px; padding: 32px;
}
.contact-info h3 {
  font-size: 22px; font-weight: 700; margin-bottom: 24px; letter-spacing: -0.01em;
}
.contact-row {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 12px 0; border-bottom: 1px solid #1f2733;
}
.contact-row:last-child { border-bottom: 0; }
.contact-row .ic {
  width: 36px; height: 36px; border-radius: 8px;
  background: rgba(79, 140, 255, 0.12); color: #6ea1ff;
  display: grid; place-items: center; flex-shrink: 0;
}
.contact-row .label {
  font-size: 12px; color: #5a6577; text-transform: uppercase;
  letter-spacing: 0.06em; margin-bottom: 4px;
}
.contact-row .value { font-size: 14.5px; color: #e8edf3; font-weight: 500; }
.contact-row .value a { color: #6ea1ff; text-decoration: none; }
.contact-row .value a:hover { text-decoration: underline; }

.contact-form {
  background: #161c26; border: 1px solid #1f2733;
  border-radius: 14px; padding: 32px;
}
.contact-form h3 {
  font-size: 22px; font-weight: 700; margin-bottom: 6px; letter-spacing: -0.01em;
}
.form-sub { font-size: 13.5px; color: #8b95a5; margin-bottom: 22px; }
.field { margin-bottom: 16px; }
.field label {
  display: block; font-size: 12.5px; font-weight: 500; color: #8b95a5; margin-bottom: 6px;
}
.field input, .field textarea, .field select {
  width: 100%; padding: 10px 13px;
  background: #11161e; border: 1px solid #1f2733;
  color: #e8edf3; border-radius: 8px;
  font: inherit; font-size: 14px; outline: 0;
  box-sizing: border-box;
}
.field input:focus, .field textarea:focus, .field select:focus { border-color: #4f8cff; }
.field textarea { min-height: 88px; resize: vertical; line-height: 1.5; }
.form-error {
  padding: 8px 12px; font-size: 13px; color: #ff5d8f;
  background: rgba(255,93,143,0.08);
  border-radius: 6px; margin-bottom: 12px;
}
.form-success {
  padding: 12px 14px; font-size: 13.5px; color: #3ecf8e;
  background: rgba(62,207,142,0.1);
  border: 1px solid rgba(62,207,142,0.3);
  border-radius: 8px;
}
.reset-link {
  margin-left: 8px;
  background: transparent; border: 0; color: #6ea1ff;
  cursor: pointer; font: inherit; font-size: 13px; text-decoration: underline;
}
.btn-primary {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 12px 22px; border-radius: 8px;
  font-weight: 500; font-size: 14px; cursor: pointer;
  background: #4f8cff; color: #fff; border: 1px solid #4f8cff;
  transition: all 0.15s;
}
.btn-primary:hover { background: #6ea1ff; border-color: #6ea1ff; }
.contact-submit { width: 100%; padding: 12px; }

/* ─── Responsive (per STYLE_NOTES §1/§2 breakpoints) ─── */
@media (max-width: 980px) {
  .container { padding: 0 24px; }
  .hero-title { font-size: 44px; }
  .hero-sub { font-size: 17px; }
  .section-mission h2, .team-section h2, .section-legal h2, .section-contact h2 { font-size: 32px; }
  .mission-grid { grid-template-columns: 1fr; gap: 36px; }
  .team-grid { grid-template-columns: repeat(2, 1fr); }
  .legal-grid { grid-template-columns: 1fr; }
  .contact-grid { grid-template-columns: 1fr; }
}
@media (max-width: 640px) {
  .container { padding: 0 18px; }
  .hero { padding: 48px 0 40px; }
  .hero-title { font-size: 30px; }
  .hero-sub { font-size: 15.5px; }
  .section-mission, .team-section, .section-legal, .section-contact { padding: 56px 0; }
  .section-head { margin-bottom: 36px; }
  .section-mission h2, .team-section h2, .section-legal h2, .section-contact h2 { font-size: 26px; }
  .mission-stats { grid-template-columns: 1fr 1fr; gap: 10px; }
  .mission-stat { padding: 18px; }
  .mission-stat .v { font-size: 26px; }
  .team-grid { grid-template-columns: 1fr; }
  .contact-info, .contact-form { padding: 24px; }
}
</style>
